import React, { useEffect, useState } from 'react';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { useApolloClient } from '@apollo/client/react';

import {
  Box,
  Button,
  COLORS,
  FontWeight,
  Input,
  Label,
  Text,
} from '@clutter/clean';

import {
  GeocodeDocument,
  GeocodeQuery,
  GeocodeQueryVariables,
} from '@graphql/platform';

import { CHALLENGER as T } from '@root/components/checkout/product_pages/challenger/tokens';
import { OSACard } from '@root/components/checkout/product_pages/subcomponents/osa_card';
import { StorageCheckoutStepProps } from '@root/components/checkout/types';

import { useZipValidatedFunnelEvent } from '@utils/hooks/funnel_events/use_zip_validated_funnel_event';
import { reportError } from '@utils/monitoring';

const ZIP_REGEX = /^\d{5}$|^[a-z0-9]{6}$/i;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

type FieldTone = 'default' | 'brand' | 'error';

const ChallengerField = styled.div<{ $tone: FieldTone }>`
  display: flex;
  align-items: center;
  gap: ${T.space[16]};
  width: 100%;
  max-width: 364px;
  min-width: 220px;
  padding: ${T.space[4]} ${T.space[4]} ${T.space[4]} ${T.space[16]};
  box-sizing: border-box;
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'brand'
        ? T.color.borderBrand
        : $tone === 'error'
          ? T.color.textError
          : T.color.borderDefault};
`;

const ChallengerInput = styled.input`
  flex: 1 0 0;
  min-width: 0;
  appearance: none;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textPrimary};

  &::placeholder {
    color: ${T.color.textSubtle};
    font-weight: ${FontWeight.Regular};
  }
`;

/**
 * Figma 707:6947 Button — Demi Bold 16/16, h48, radius 8.
 * Loading: pl-16 pr-24 + 24px loader (gap 8). Idle: px-24.
 */
const ApplyButton = styled.button<{ $loading?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${T.space[8]};
  flex-shrink: 0;
  height: 48px;
  padding: ${({ $loading }) =>
    $loading ? `0 ${T.space[24]} 0 ${T.space[16]}` : `0 ${T.space[24]}`};
  border: none;
  border-radius: ${T.radius.sm};
  background: ${T.color.textBrand};
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textInverse};
  white-space: nowrap;

  &:disabled {
    cursor: wait;
  }

  &:hover:not(:disabled) {
    opacity: 0.92;
  }
`;

/** Figma icon/loader — 24px frame, ~19px white arc. */
const Loader = styled.span`
  position: relative;
  display: block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 19px;
    height: 19px;
    margin: -9.5px 0 0 -9.5px;
    box-sizing: border-box;
    border: 2px solid ${T.color.textInverse};
    border-right-color: transparent;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
`;

const Caption = styled.p<{ $tone: 'error' | 'success' }>`
  margin: 0;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${({ $tone }) =>
    $tone === 'error' ? T.color.textError : T.color.textBrandDark};
`;

const ChallengerForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  max-width: 364px;
`;

type ZipProps = StorageCheckoutStepProps & {
  /** Challenger edit card (Figma 707:6434 / 707:6940 / 894:16647). */
  appearance?: 'classic' | 'challenger';
};

export const Zip: React.FC<ZipProps> = (props) => {
  const {
    scrollToStep,
    values: { zip },
    onChange,
    appearance = 'classic',
  } = props;

  const [localZip, setLocalZip] = useState(zip);
  const [showOSAMessage, setShowOSAMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zipError, setZipError] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);
  const [focused, setFocused] = useState(false);
  const client = useApolloClient();

  useEffect(() => {
    setLocalZip(zip);
  }, [zip]);

  const handleZipChange = ({
    currentTarget: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setLocalZip(value);
    onChange('zipValidated', false);
    if (showOSAMessage) setShowOSAMessage(false);
    if (zipError) setZipError(undefined);
    if (success) setSuccess(false);
  };

  const trackZipValidated = useZipValidatedFunnelEvent();

  const handleZipSubmit = async () => {
    if (!localZip || !ZIP_REGEX.test(localZip)) {
      setZipError(
        appearance === 'challenger'
          ? 'Please enter a valid ZIP code.'
          : 'Please enter a complete zip code',
      );
      setSuccess(false);
      return;
    }
    try {
      setLoading(true);
      setSuccess(false);
      const { data } = await client.query<GeocodeQuery, GeocodeQueryVariables>({
        query: GeocodeDocument,
        variables: { zip: localZip },
      });
      const storageEligible = !!data?.geocode?.eligibleForDoorToDoor;
      if (storageEligible) {
        trackZipValidated({ zip: localZip!, service: 'storage' });
        onChange('zip', localZip);
        onChange('zipValidated', true);
        if (appearance === 'challenger') {
          setSuccess(true);
        }
        scrollToStep('next');
      } else if (data && !data.geocode) {
        setZipError('Please enter a valid ZIP code.');
      } else {
        setShowOSAMessage(true);
      }
    } catch (e: any) {
      reportError(e);
    } finally {
      setLoading(false);
    }
  };

  if (appearance === 'challenger') {
    const tone: FieldTone = zipError
      ? 'error'
      : loading || focused || success
        ? 'brand'
        : 'default';

    return (
      <>
        <ChallengerForm
          onSubmit={(e) => {
            e.preventDefault();
            handleZipSubmit();
          }}
        >
          <ChallengerField $tone={tone}>
            <ChallengerInput
              name="zip"
              value={localZip ?? ''}
              onChange={handleZipChange}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              aria-label="ZIP code"
              autoComplete="postal-code"
              inputMode="numeric"
            />
            <ApplyButton type="submit" disabled={loading} $loading={loading}>
              {loading && <Loader aria-hidden />}
              Apply
            </ApplyButton>
          </ChallengerField>
          {zipError && <Caption $tone="error">{zipError}</Caption>}
          {success && !zipError && localZip && (
            <Caption $tone="success">
              ZIP updated to {localZip}. Pricing now reflects your address.
            </Caption>
          )}
        </ChallengerForm>
        {showOSAMessage && <OSACard />}
      </>
    );
  }

  return (
    <Box margin={['0 0 48px', null, '0 0 72px']}>
      <Box margin="0 0 24px">
        <Text.Title size="small" color={COLORS.tealDark}>
          What’s your ZIP code?
        </Text.Title>
      </Box>
      <Label>ZIP Code</Label>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleZipSubmit();
        }}
      >
        <Box.Flex gap="16px">
          <Box.FlexItem>
            <Input
              name="zip"
              value={localZip ?? ''}
              onChange={handleZipChange}
              style={{ width: '100%' }}
            />
          </Box.FlexItem>
          <Box.FlexItem width="130px">
            <Button type="submit" loading={loading}>
              Submit
            </Button>
          </Box.FlexItem>
        </Box.Flex>
      </form>
      <Box minHeight="19px" margin="8px 0 0">
        <Text.Caption color={COLORS.toucan} weight={FontWeight.Medium}>
          {zipError}
        </Text.Caption>
      </Box>
      {showOSAMessage && <OSACard />}
    </Box>
  );
};
